// TGCLOUD-1818 Phase 4 — recognise an identifier typed into the free-text box
// and offer the equivalent chip.
//
// The reason exact match matters on an identifier is that the user already
// KNOWS the exact value. Recognising the shape and offering the right operator
// saves them choosing one, and saves the false hits a substring match gives on
// a number.
//
// WHAT SHIPPED, AND WHAT DID NOT.
//
// Only the TrakGene family ID is here. The brief asked for these to be
// deployment configuration, because an MRN at one trust is not an MRN at
// another and NHS, Garvan and NL Health formats all differ — but the Phase 0
// audit found no mechanism to hang that on. The only field configurability in
// the product is three relabellable *strings* (mobile, hospitalNumber,
// healthcareId) on securitysettings/grouppreferences; there is no stored,
// admin-editable identifier PATTERN anywhere, and the scoping unit is
// userGroup rather than deployment. Inventing one was explicitly out of scope.
//
// So the family ID pattern is hardcoded, which is safe because it is
// TrakGene's OWN format, generated by userController.js:
//   const familyId = `G-${userGroupId}-${currentId}`      ->  G-0-01520
//   const proBandId = `${familyId}-1`                     ->  G-0-01520-1
// Anything else needs the configuration mechanism first.

/**
 * @typedef  {Object} IdentifierPattern
 * @property {string} id        stable key, for tests and future config
 * @property {RegExp} test      must be anchored; matched against the trimmed term
 * @property {string} field     FilterField.key the suggestion targets
 * @property {string} operator  the operator to offer
 */

/**
 * The recognised shapes.
 *
 * `familyMemberId` is matched before `familyId` in spirit but they cannot
 * overlap: the member form has a fourth segment and both are fully anchored,
 * so exactly one can match any given string. That matters because two matches
 * means NO suggestion (see below), and an accidental overlap would silence a
 * suggestion that should have appeared.
 */
export const IDENTIFIER_PATTERNS = Object.freeze([
  {
    id: 'trakgene-family-id',
    test: /^G-\d+-\d+$/i,
    field: 'familyId',
    operator: 'is'
  }
]);

/**
 * The single suggestion for a search term, or null.
 *
 * ONE suggestion, never a list. Pressing Enter already means "search
 * everything for this", so a second row saying so would be noise. And if two
 * patterns both match, the honest answer is that we cannot tell which the user
 * meant — so we say nothing rather than guess. That is a real constraint once
 * deployment-configured patterns exist and two trusts' formats collide; it is
 * unreachable with one pattern, which is exactly why it is tested.
 *
 * Deliberately does NOT consult the data. A pattern that matches no record
 * still produces a valid chip and an honest empty state ("No matching people.
 * 1 filter active."), which is more useful than silently withholding the
 * suggestion and leaving the user to wonder.
 *
 * @param {string} term the raw free-text value
 * @param {Array<{key: string}>} fields the served field descriptors
 * @param {IdentifierPattern[]} [patterns] override, so the ambiguity rule can be
 *        tested — with one shipped pattern the two-match case is otherwise
 *        unreachable, and an untestable rule is one nobody notices breaking.
 * @returns {{field: string, operator: string, value: string, patternId: string}|null}
 */
export const suggestIdentifierCriterion = (
  term,
  fields,
  patterns = IDENTIFIER_PATTERNS
) => {
  const value = String(term ?? '').trim();
  if (!value) return null;

  const available = new Set((fields || []).map((f) => f.key));
  const matches = (patterns || []).filter(
    (pattern) => pattern.test.test(value) && available.has(pattern.field)
  );

  // Zero matches: nothing to offer. Two or more: we cannot tell, so stay quiet.
  if (matches.length !== 1) return null;

  const [{ field, operator, id }] = matches;
  return { field, operator, value, patternId: id };
};
